import React, { useState, useEffect } from 'react';
import { 
  XCircleIcon, 
  BookOpenIcon, 
  ClockIcon, 
  AcademicCapIcon,
  ArrowLeftIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';
import { renderMathContent } from '../utils/renderMath';
import { useAuth } from '../contexts/AuthContext';
import { getKoreanExamName } from '../utils/examNameMapping';
import CommonHeader from './common/CommonHeader';

const WrongAnswerNote = () => {
  const { user } = useAuth();
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState('all');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 오답 데이터 로드
  useEffect(() => {
    loadWrongAnswers();
  }, [user]);

  const loadWrongAnswers = () => {
    setIsLoading(true);
    try {
      const savedWrongAnswers = JSON.parse(localStorage.getItem(`wrongAnswers_${user?.id}`) || '[]');
      setWrongAnswers(savedWrongAnswers);
    } catch (error) {
      console.error('오답노트 로드 오류:', error);
      setWrongAnswers([]);
    }
    setIsLoading(false);
  };

  // 수식 렌더링
  // 수식 렌더링: 공용 유틸 사용 (src/utils/renderMath.jsx)

  // 시험 타입별 필터링
  const filteredWrongAnswers = selectedExamType === 'all' 
    ? wrongAnswers 
    : wrongAnswers.filter(item => item.examType === selectedExamType);

  // 시험 타입 목록 생성
  const examTypes = [...new Set(wrongAnswers.map(item => item.examType))];

  // 오답 삭제 후 다음 문제로 이동
  const deleteWrongAnswer = (questionId) => {
    const updatedWrongAnswers = wrongAnswers.filter(item => item.questionId !== questionId);
    setWrongAnswers(updatedWrongAnswers);
    localStorage.setItem(`wrongAnswers_${user?.id}`, JSON.stringify(updatedWrongAnswers));
    
    // 다음 오답 문제 찾기
    const currentIndex = filteredWrongAnswers.findIndex(item => item.questionId === questionId);
    const nextQuestion = filteredWrongAnswers[currentIndex + 1] || filteredWrongAnswers[0];
    
    // 다음 문제가 있으면 이동, 없으면 목록으로 돌아가기
    if (nextQuestion && updatedWrongAnswers.some(item => item.questionId === nextQuestion.questionId)) {
      setSelectedQuestion(nextQuestion);
    } else {
      setSelectedQuestion(null);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (selectedQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100">
        <CommonHeader 
          title="오답노트"
          subtitle="문제 상세 보기"
        />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setSelectedQuestion(null)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                <span>오답노트로 돌아가기</span>
              </button>
              <button
                onClick={() => deleteWrongAnswer(selectedQuestion.questionId)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                <span>삭제</span>
              </button>
            </div>

            <div className="space-y-6">
              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">문제</h3>
                <div className="text-gray-700 leading-relaxed">
                  {renderMathContent(selectedQuestion.questionText)}
                </div>
              </div>

              {selectedQuestion.image && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <img
                    src={selectedQuestion.image}
                    alt="문제 이미지"
                    className="w-auto h-auto max-w-full max-h-56 sm:max-h-72 md:max-h-80 object-contain rounded"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">선택지</h4>
                  <div className="space-y-2">
                    {selectedQuestion.options.map((option, index) => (
                      <div 
                        key={index}
                        className={`p-3 rounded-lg border ${
                          index + 1 === selectedQuestion.correctAnswer 
                            ? 'border-green-500 bg-green-50' 
                            : index + 1 === selectedQuestion.userAnswer
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{index + 1}.</span>
                          <span>{renderMathContent(typeof option === 'object' ? option.text || option : option)}</span>
                          {index + 1 === selectedQuestion.correctAnswer && (
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          )}
                          {index + 1 === selectedQuestion.userAnswer && selectedQuestion.userAnswer !== selectedQuestion.correctAnswer && (
                            <XCircleIcon className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">해설</h4>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <div className="text-gray-700 leading-relaxed">
                      {renderMathContent(selectedQuestion.explanation || '해설이 없습니다.')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">과목:</span> {getKoreanExamName(selectedQuestion.examType)}
                  </div>
                  <div>
                    <span className="font-medium">출제:</span> 
                    {selectedQuestion.year && ` ${selectedQuestion.year}년`}
                    {selectedQuestion.session && selectedQuestion.session !== '진단' && ` ${selectedQuestion.session}회`}
                    {selectedQuestion.questionNumber && ` ${selectedQuestion.questionNumber}번`}
                  </div>
                  <div>
                    <span className="font-medium">틀린 날짜:</span> {formatDate(selectedQuestion.wrongDate)}
                  </div>
                  {selectedQuestion.attempts > 1 && (
                    <div>
                      <span className="font-medium">재시도:</span> {selectedQuestion.attempts}회
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100">
      <CommonHeader 
        title="오답노트"
        subtitle="틀린 문제를 다시 확인하고 학습하세요"
        rightContent={
          <div className="flex items-center space-x-2 text-gray-700">
            <BookOpenIcon className="w-5 h-5" />
            <span className="font-medium">{filteredWrongAnswers.length}개 문제</span>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-4">
            <span className="text-gray-700 font-medium">과목 필터:</span>
            <select
              value={selectedExamType}
              onChange={(e) => setSelectedExamType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">전체 ({wrongAnswers.length}개)</option>
              {examTypes.map(examType => (
                <option key={examType} value={examType}>
                  {getKoreanExamName(examType)} ({wrongAnswers.filter(item => item.examType === examType).length}개)
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Wrong Answers List */}
        {filteredWrongAnswers.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <AcademicCapIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {selectedExamType === 'all' ? '아직 틀린 문제가 없습니다' : '해당 과목에서 틀린 문제가 없습니다'}
            </h3>
            <p className="text-gray-600">시험을 풀면 틀린 문제가 자동으로 저장됩니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredWrongAnswers.map((wrongAnswer) => (
              <div key={wrongAnswer.questionId} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        <XCircleIcon className="w-5 h-5 text-red-500" />
                        <span className="text-sm font-medium text-gray-900">{getKoreanExamName(wrongAnswer.examType)}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 ml-7">
                        {wrongAnswer.year && <span>{wrongAnswer.year}년</span>}
                        {wrongAnswer.session && wrongAnswer.session !== '진단' && <span>{wrongAnswer.session}회</span>}
                        {wrongAnswer.questionNumber && <span>{wrongAnswer.questionNumber}번 문제</span>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <ClockIcon className="w-4 h-4" />
                      <span>{formatDate(wrongAnswer.wrongDate)}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-gray-900 font-medium line-clamp-3">
                      {renderMathContent(wrongAnswer.questionText)}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-red-600">
                        내 답: {wrongAnswer.userAnswer}번
                      </span>
                      <span className="text-green-600">
                        정답: {wrongAnswer.correctAnswer}번
                      </span>
                    </div>
                    
                    <button
                      onClick={() => setSelectedQuestion(wrongAnswer)}
                      className="flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <EyeIcon className="w-4 h-4" />
                      <span>자세히 보기</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WrongAnswerNote;